import React, { isValidElement, cloneElement, forwardRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { svgUtil, propsUtil, INFO, SUCCESS, WARNING, DANGER } from '@asphalt-react/helper';
import { Consumer } from '@asphalt-react/context';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

/**
 * size values
 */
var sizes = ["s", "m", "l"];
var svgSizes = {
  sm: {
    height: "14",
    width: "14"
  },
  md: {
    height: "16",
    width: "16"
  },
  lg: {
    height: "20",
    width: "20"
  }
};

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Button__2uJ7L {\n  /* default custom properties */\n  --button-border-radius: var(--roundness-1, 2px);\n  --button-primary-fill-color: var(\n    --interactive-fill-brand-default,\n    hsla(127, 100%, 33%, 1)\n  );\n  --button-secondary-fill-color: var(--interactive-fill-alternate, transparent);\n  --button-outline-fill-color: var(--interactive-fill-alternate, transparent);\n  --button-nude-fill-color: var(--interactive-fill-alternate, transparent);\n  --button-danger-fill-color: var(\n    --support-danger-default,\n    hsla(12, 92%, 45%, 1)\n  );\n\n  --button-primary-active-color: var(\n    --interactive-fill-brand-active,\n    hsla(127, 100%, 27%, 1)\n  );\n  --button-primary-hover-color: var(\n    --interactive-fill-brand-hover,\n    hsla(127, 57%, 47%, 1)\n  );\n  --button-disabled-color: var(--interactive-disabled, hsla(0, 0%, 86%, 1));\n\n  --button-secondary-active-color: var(\n    --interactive-fill-active,\n    hsla(220, 17%, 93%, 1)\n  );\n  --button-secondary-hover-color: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n\n  --button-danger-active-color: var(\n    --support-danger-active,\n    hsla(12, 92%, 36%, 1)\n  );\n  --button-danger-hover-color: var(\n    --support-danger-hover,\n    hsla(12, 76%, 56%, 1)\n  );\n\n  --button-outline-active-color: var(\n    --interactive-fill-selected-active,\n    hsla(219, 42%, 24%, 1)\n  );\n  --button-outline-hover-color: var(\n    --interactive-fill-selected-hover,\n    hsla(219, 23%, 44%, 1)\n  );\n\n  --button-nude-active-color: var(\n    --interactive-fill-active,\n    hsla(220, 17%, 93%, 1)\n  );\n  --button-nude-hover-color: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n\n  --button-primary-font: var(--text-inverse, hsla(0, 0%, 100%, 1));\n  --button-secondary-font: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --button-danger-font: var(--text-inverse, hsla(0, 0%, 100%, 1));\n  --button-outline-font: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --button-outline-hover-font: var(--text-inverse, hsla(0, 0%, 100%, 1));\n\n  --button-border-focus: var(--interactive-focus, hsla(219, 42%, 18%, 1));\n  --button-outline-border-danger: var(\n    --interactive-border-support,\n    hsla(12, 92%, 36%, 1)\n  );\n\n  --button-border-width: var(--border-width, 1px);\n\n  --button-text-disabled: var(--text-disabled, hsla(0, 0%, 62%, 1));\n\n  --button-l-fontsize: var(--label-l, 1.13rem);\n  --button-m-fontsize: var(--label-m, 1rem);\n  --button-s-fontsize: var(--label-s, 0.88rem);\n  --nude-button-s-fontsize: var(--label-l, 1.13rem);\n  --nude-button-xs-fontsize: var(--label-m, 1rem);\n  --nude-button-xxs-fontsize: var(--label-s, 0.88rem);\n\n  --button-inverse-icon-color: var(--icon-inverse, hsla(0, 0%, 100%, 1));\n  --button-primary-icon-color: var(--icon-primary, hsla(220, 42%, 24%, 1));\n\n  --icon-disabled-color: var(--icon-disabled, hsla(0, 0%, 62%, 1));\n\n  /* custom support nude buttons */\n  --nude-info-fill-color: var(--support-info-default, hsla(209, 100%, 42%, 1));\n  --nude-info-hover-color: var(--support-info-hover, hsla(209, 72%, 54%, 1));\n  --nude-info-active-color: var(--support-info-active, hsla(209, 100%, 34%, 1));\n\n  --nude-success-fill-color: var(\n    --support-success-default,\n    hsla(159, 97%, 27%, 1)\n  );\n  --nude-success-hover-color: var(\n    --support-success-hover,\n    hsla(159, 51%, 41%, 1)\n  );\n  --nude-success-active-color: var(\n    --support-success-active,\n    hsla(159, 98%, 21%, 1)\n  );\n\n  --nude-warning-fill-color: var(\n    --support-warning-default,\n    hsla(33, 100%, 56%, 1)\n  );\n  --nude-warning-hover-color: var(\n    --support-warning-hover,\n    hsla(33, 100%, 65%, 1)\n  );\n  --nude-warning-active-color: var(\n    --support-warning-active,\n    hsla(33, 79%, 45%, 1)\n  );\n\n  --nude-danger-fill-color: var(\n    --support-danger-default,\n    hsla(12, 92%, 45%, 1)\n  );\n  --nude-danger-hover-color: var(--support-danger-hover, hsla(12, 76%, 56%, 1));\n  --nude-danger-active-color: var(\n    --support-danger-active,\n    hsla(12, 92%, 36%, 1)\n  );\n\n  --nude-support-font-color: var(--text-inverse, hsla(0, 0%, 100%, 1));\n\n  display: inline-flex;\n  justify-content: center;\n  white-space: nowrap;\n  text-align: center;\n  align-items: center;\n  border-color: transparent;\n  border-width: var(--button-border-width);\n  font-weight: 600;\n  line-height: initial;\n  border-radius: var(--button-border-radius);\n  box-sizing: border-box;\n  outline: none !important;\n  outline-style: none;\n}\n.Button__2uJ7L.stretch__3PMNL {\n  width: 100%;\n}\n\n/* \n* Pointer events none is added to stop event propagation from svg (or path) back to the user \n*/\n\n.IconButton__2P43U svg {\n  pointer-events: none;\n}\n\n/* primary styles */\n.primary__2TJ3j {\n  background-color: var(--button-primary-fill-color);\n  color: var(--button-primary-font);\n}\n\n.primary__2TJ3j svg path {\n  fill: var(--button-inverse-icon-color);\n}\n\n.primary__2TJ3j:hover {\n  background-color: var(--button-primary-hover-color);\n}\n\n.primary__2TJ3j:focus {\n  background-color: var(--button-primary-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n}\n\n.primary__2TJ3j:active {\n  background-color: var(--button-primary-active-color);\n}\n\n/* secondary styles */\n.secondary__1uNCG {\n  background-color: var(--button-secondary-fill-color);\n  color: var(--button-secondary-font);\n}\n\n.secondary__1uNCG svg path,\n.outline__1mgE1 svg path {\n  fill: var(--button-primary-icon-color);\n}\n\n.secondary__1uNCG:hover {\n  background-color: var(--button-secondary-hover-color);\n}\n\n.secondary__1uNCG:focus {\n  background-color: var(--button-secondary-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n}\n\n.secondary__1uNCG:active {\n  background-color: var(--button-secondary-active-color);\n}\n\n/* outline styles */\n.outline__1mgE1 {\n  background-color: var(--button-outline-fill-color);\n  border: 1px solid var(--button-border-focus);\n  color: var(--button-outline-font);\n}\n\n.outline__1mgE1:hover {\n  background-color: var(--button-outline-hover-color);\n  color: var(--button-outline-hover-font);\n}\n\n.outline__1mgE1:focus {\n  background-color: var(--button-outline-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n  color: var(--button-outline-hover-font);\n}\n\n.outline__1mgE1:active {\n  background-color: var(--button-outline-active-color);\n}\n\n/* nude styles */\n.nude__146BB {\n  background: var(--button-nude-fill-color);\n  color: var(--button-secondary-font);\n}\n\n.nude__146BB:hover {\n  background-color: var(--button-nude-hover-color);\n}\n\n.nude__146BB:focus {\n  background-color: var(--button-nude-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n}\n\n.nude__146BB:active {\n  background-color: var(--button-nude-active-color);\n}\n\n/* danger styles */\n.primary__2TJ3j.danger__10-YO,\n.IconButton__2P43U.danger__10-YO:not(.nude__146BB) {\n  background-color: var(--button-danger-fill-color);\n  color: var(--button-danger-font);\n}\n\n.primary__2TJ3j.danger__10-YO svg path,\n.IconButton__2P43U.danger__10-YO svg path {\n  fill: var(--button-danger-font);\n}\n\n.primary__2TJ3j.danger__10-YO:hover,\n.outline__1mgE1.danger__10-YO:hover,\n.IconButton__2P43U.danger__10-YO:not(.nude__146BB):hover:enabled \n/* enabled applies when disabled exists. \nIt applies styles only when in enabled state   */ {\n  background-color: var(--button-danger-hover-color);\n}\n\n.primary__2TJ3j.danger__10-YO:focus,\n.outline__1mgE1.danger__10-YO:focus,\n.IconButton__2P43U.danger__10-YO:not(.nude__146BB):focus {\n  background-color: var(--button-danger-hover-color);\n  border-width: var(--button-border-width);\n  border-color: var(--button-border-focus);\n}\n\n.primary__2TJ3j.danger__10-YO:active,\n.outline__1mgE1.danger__10-YO:active,\n.IconButton__2P43U.danger__10-YO:not(.nude__146BB):active {\n  background-color: var(--button-danger-active-color);\n}\n\n.secondary__1uNCG.danger__10-YO,\n.nude__146BB.danger__10-YO,\n.outline__1mgE1.danger__10-YO {\n  color: var(--button-danger-fill-color);\n}\n\n.secondary__1uNCG.danger__10-YO svg path,\n.nude__146BB.danger__10-YO svg path,\n.IconButton__2P43U.nude__146BB.danger__10-YO svg path {\n  fill: var(--button-danger-fill-color);\n}\n\n.outline__1mgE1.danger__10-YO {\n  border-color: var(--button-outline-border-danger);\n}\n\n.outline__1mgE1.danger__10-YO:hover,\n.outline__1mgE1.danger__10-YO:focus,\n.outline__1mgE1.danger__10-YO:active {\n  color: var(--button-outline-hover-font);\n}\n\n/* disabled styles */\n.primary__2TJ3j:disabled,\n.secondary__1uNCG:disabled,\n.outline__1mgE1:disabled,\n.primary__2TJ3j.danger__10-YO:disabled,\n.outline__1mgE1.danger__10-YO:disabled,\n.nude__146BB:disabled {\n  background-color: var(--button-disabled-color);\n  color: var(--button-text-disabled);\n  cursor: not-allowed;\n  border-color: transparent;\n}\n\n.nude__146BB:disabled {\n  background-color: transparent;\n}\n\n.primary__2TJ3j:disabled svg path,\n.secondary__1uNCG:disabled svg path,\n.nude__146BB:disabled svg path,\n.danger__10-YO:disabled svg path,\n.IconButton__2P43U.nude__146BB:disabled svg path {\n  fill: var(--icon-disabled-color);\n}\n\n/* \n* Pointer events none is added to stop event propagation from svg (or path) back to the user \n*/\n\n.qualifierContainer__aq5oB {\n  display: flex;\n  pointer-events: none;\n}\n\n.qualifierContainer__aq5oB {\n  margin-right: 0.5rem;\n}\n\n.end__3SxYf {\n  flex-direction: row-reverse;\n}\n\n.end__3SxYf > .qualifierContainer__aq5oB {\n  margin-left: 0.5rem;\n  margin-right: 0;\n}\n\n/* sizes */\n.l__19nHj {\n  padding: 0.75rem 1.25rem;\n  font-size: var(--button-l-fontsize);\n}\n\n.l__19nHj.qualifier__2hZAj {\n  padding: 0.75rem;\n}\n\n.m__2A91K {\n  padding: 0.5rem 1rem;\n  font-size: var(--button-m-fontsize);\n}\n\n.m__2A91K.qualifier__2hZAj {\n  padding: 0.5rem;\n}\n\n.s__zAvWh {\n  padding: 0.25rem 0.75rem;\n  font-size: var(--button-s-fontsize);\n}\n\n.s__zAvWh.qualifier__2hZAj {\n  padding: 0.25rem;\n}\n\n.outline__1mgE1.l__19nHj,\n.nude__146BB.l__19nHj {\n  padding-top: 0.25rem;\n  padding-bottom: 0.25rem;\n}\n\n.outline__1mgE1.m__2A91K,\n.nude__146BB.m__2A91K {\n  padding-top: 0.125rem;\n  padding-bottom: 0.125rem;\n}\n\n.outline__1mgE1.s__zAvWh,\n.nude__146BB.s__zAvWh {\n  padding-top: 0;\n  padding-bottom: 0;\n}\n\n.IconButton__2P43U.l__19nHj {\n  padding: 1rem;\n}\n\n.IconButton__2P43U.m__2A91K {\n  padding: 0.75rem 1rem;\n}\n\n.IconButton__2P43U.s__zAvWh {\n  padding: 0.5rem 1rem;\n}\n\n.IconButton__2P43U.nude__146BB {\n  padding: 0.25rem;\n}\n\n/* support nude button styles for Flag component */\n.nude__146BB.support__1L2sX {\n  color: var(--nude-support-font-color);\n}\n\n/* danger */\n.nude__146BB.support__1L2sX.supportDanger__3MtRH {\n  background-color: var(--nude-danger-fill-color);\n}\n\n.nude__146BB.support__1L2sX.supportDanger__3MtRH:hover {\n  background-color: var(--nude-danger-hover-color);\n}\n\n.nude__146BB.support__1L2sX.supportDanger__3MtRH:active {\n  background-color: var(--nude-danger-active-color);\n}\n\n/* warning */\n.nude__146BB.support__1L2sX.supportWarning__17bkH {\n  background-color: var(--nude-warning-fill-color);\n}\n\n.nude__146BB.support__1L2sX.supportWarning__17bkH:hover {\n  background-color: var(--nude-warning-hover-color);\n}\n\n.nude__146BB.support__1L2sX.supportWarning__17bkH:active {\n  background-color: var(--nude-warning-active-color);\n}\n\n/* success */\n.nude__146BB.support__1L2sX.supportSuccess__1CcDh {\n  background-color: var(--nude-success-fill-color);\n}\n\n.nude__146BB.support__1L2sX.supportSuccess__1CcDh:hover {\n  background-color: var(--nude-success-hover-color);\n}\n\n.nude__146BB.support__1L2sX.supportSuccess__1CcDh:active {\n  background-color: var(--nude-success-active-color);\n}\n\n/* info */\n.nude__146BB.support__1L2sX.supportInfo__ljRjm {\n  background-color: var(--nude-info-fill-color);\n}\n\n.nude__146BB.support__1L2sX.supportInfo__ljRjm:hover {\n  background-color: var(--nude-info-hover-color);\n}\n\n.nude__146BB.support__1L2sX.supportInfo__ljRjm:active {\n  background-color: var(--nude-info-active-color);\n}\n\n/* rtl styles */\n*[dir=\"rtl\"] .qualifierContainer__aq5oB {\n  margin-left: 0.5rem;\n  margin-right: 0;\n}\n\n*[dir=\"rtl\"] .end__3SxYf > .qualifierContainer__aq5oB {\n  margin-right: 0.5rem;\n  margin-left: 0;\n}\n";
var styles = {"Button":"Button__2uJ7L","stretch":"stretch__3PMNL","IconButton":"IconButton__2P43U","primary":"primary__2TJ3j","secondary":"secondary__1uNCG","outline":"outline__1mgE1","nude":"nude__146BB","danger":"danger__10-YO","qualifierContainer":"qualifierContainer__aq5oB","end":"end__3SxYf","l":"l__19nHj","qualifier":"qualifier__2hZAj","m":"m__2A91K","s":"s__zAvWh","support":"support__1L2sX","supportDanger":"supportDanger__3MtRH","supportWarning":"supportWarning__17bkH","supportSuccess":"supportSuccess__1CcDh","supportInfo":"supportInfo__ljRjm"};
styleInject(css_248z);

var sizeClass = function sizeClass(size) {
  switch (size) {
    case "l":
      return styles.l;

    case "m":
      return styles.m;

    case "s":
      return styles.s;

    default:
      return styles.m;
  }
};
/**
 * Create css classes from props
 *
 * @param {object} param custom props object
 * @returns {string}
 */


var createBtnClasses = function createBtnClasses(_ref) {
  var size = _ref.size;
  return cn(styles.Button, sizeClass(size));
};

var svgSize = function svgSize(size) {
  switch (size) {
    case "s":
      return svgSizes.sm;

    case "m":
      return svgSizes.md;

    case "l":
      return svgSizes.lg;

    default:
      return svgSizes.md;
  }
};
/**
 * Appends the dimensions of svg according to the size
 *
 * @param {object} param
 * @returns {object}
 */


var SvgNormalizer = function SvgNormalizer(_ref2) {
  var size = _ref2.size,
      children = _ref2.children;

  if ( /*#__PURE__*/isValidElement(children) && typeof children.type === "function" && !children.props.mdxType) {
    if (svgUtil.isSVG(children.type())) return /*#__PURE__*/cloneElement(children.type(), svgSize(size));
  }

  if ( /*#__PURE__*/isValidElement(children) && children.props.mdxType && typeof children.props.originalType === "function") {
    return /*#__PURE__*/cloneElement(children.props.originalType(), svgSize(size));
  }

  if (svgUtil.isSVG(children)) {
    return /*#__PURE__*/cloneElement(children, svgSize(size));
  }

  return children;
};

var createPropOccurValidator = propsUtil.createPropOccurValidator,
    validateSize = propsUtil.validateSize;
var isSVG = svgUtil.isSVG;
var Button = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      size = _ref.size,
      primary = _ref.primary,
      secondary = _ref.secondary,
      outline = _ref.outline,
      danger = _ref.danger,
      qualifier = _ref.qualifier,
      qualifierStart = _ref.qualifierStart,
      qualifierEnd = _ref.qualifierEnd,
      nude = _ref.nude,
      stretch = _ref.stretch,
      props = _objectWithoutProperties(_ref, ["children", "size", "primary", "secondary", "outline", "danger", "qualifier", "qualifierStart", "qualifierEnd", "nude", "stretch"]);

  if (!validateSize(sizes, size)) {
    size = "m";
    console.warn("size should be one of [".concat(sizes, "]. Using fallback size '").concat(size, "'"));
  }

  if (outline && qualifier) {
    console.warn("Outline Button cannot have qualifiers. Added qualifier will be ignored");
    qualifier = false;
  }

  if (qualifier && !isSVG(qualifier)) {
    console.warn("Qualifier can only be SVG");
    qualifier = false;
  }
  /**
   * Returns style of Button
   *
   * if more than one of the below are present, falls back to `styles.secondary`
   * if nothing is provided, takes `styles.primary`
   */


  var selectBtnTypeClass = function selectBtnTypeClass() {
    var validateBtnProp = createPropOccurValidator(["primary", "secondary", "outline", "nude"]);
    var singleOccur = validateBtnProp({
      primary: primary,
      secondary: secondary,
      outline: outline,
      nude: nude
    });

    if (!singleOccur) {
      return styles.secondary;
    }

    if (secondary) {
      return styles.secondary;
    }

    if (outline) {
      return styles.outline;
    }

    if (nude) {
      return styles.nude;
    }

    return styles.primary;
  };

  var getSupportClass = function getSupportClass(supportType) {
    switch (supportType) {
      case DANGER:
        return styles.supportDanger;

      case WARNING:
        return styles.supportWarning;

      case SUCCESS:
        return styles.supportSuccess;

      case INFO:
        return styles.supportInfo;

      default:
        return null;
    }
  };

  var style = props.style,
      btnProps = _objectWithoutProperties(props, ["style"]); // Not allowing custom styles to pass to component


  var btnclasses = cn(createBtnClasses({
    size: size
  }), selectBtnTypeClass(), _defineProperty({}, styles.danger, danger), _defineProperty({}, styles.stretch, stretch), _defineProperty({}, styles.qualifier, qualifier), _defineProperty({}, styles.end, qualifier && qualifierEnd));

  var Qualifier = function Qualifier() {
    return /*#__PURE__*/React.createElement("span", {
      className: cn(styles.qualifierContainer)
    }, /*#__PURE__*/React.createElement(SvgNormalizer, {
      size: size
    }, qualifier));
  };

  return qualifier ? /*#__PURE__*/React.createElement("button", _extends({}, btnProps, {
    className: btnclasses,
    ref: ref
  }), /*#__PURE__*/React.createElement(Qualifier, null), children) : /*#__PURE__*/React.createElement(Consumer, null, function (_ref2) {
    var supportType = _ref2.supportType;
    var supportClasses = cn(btnclasses, _defineProperty({}, styles.support, !!supportType), getSupportClass(supportType));
    return /*#__PURE__*/React.createElement("button", _extends({}, btnProps, {
      className: supportClasses,
      ref: ref
    }), children);
  });
});
Button.propTypes = {
  /**
   * React node for button's caption
   */
  children: PropTypes.node.isRequired,

  /**
   * Controls size of button. accepts s, m, l for small, medium & large
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Button matches width of it's container
   */
  stretch: PropTypes.bool,

  /**
   * Renders a nude button
   */
  nude: PropTypes.bool,

  /**
   * Applies primary brand color
   */
  primary: PropTypes.bool,

  /**
   * Renders a secondary button
   */
  secondary: PropTypes.bool,

  /**
   * Renders an outline button
   */
  outline: PropTypes.bool,

  /**
   * Renders as danger button
   */
  danger: PropTypes.bool,

  /**
   * Renders an icon before or after the button's caption.
   * Accepts SVG or SVG wrapped React component
   */
  qualifier: PropTypes.element,

  /**
   * Renders qualifier before the button's caption
   */
  qualifierStart: PropTypes.bool,

  /**
   * Renders qualifier after the button's caption
   */
  qualifierEnd: PropTypes.bool
};
Button.defaultProps = {
  size: "m",
  stretch: false,
  qualifier: null,
  nude: false,
  qualifierStart: true,
  qualifierEnd: false
};

var validateSize$1 = propsUtil.validateSize,
    createPropOccurValidator$1 = propsUtil.createPropOccurValidator;
var IconButton = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var icon = _ref.icon,
      size = _ref.size,
      primary = _ref.primary,
      danger = _ref.danger,
      nude = _ref.nude,
      props = _objectWithoutProperties(_ref, ["icon", "size", "primary", "danger", "nude"]);

  if (!validateSize$1(sizes, size)) {
    size = "m";
    console.warn("size should be one of [".concat(sizes, "]. Using fallback size '").concat(size, "'"));
  }
  /**
   * Returns style of Button
   *
   * if `primary` and `nude' both are preset,
   * falls back to `styles.primary`
   *
   * @returns {Object}
   */


  var selectBtnTypeClass = function selectBtnTypeClass() {
    var validateBtnPropOccur = createPropOccurValidator$1(["primary", "nude"]);
    var singleOccur = validateBtnPropOccur({
      primary: primary,
      nude: nude
    });

    if (!singleOccur) {
      return styles.primary;
    }

    if (nude) {
      return styles.nude;
    }

    return styles.primary;
  };

  var style = props.style,
      btnProps = _objectWithoutProperties(props, ["style"]); // Not allowing custom styles to pass to component


  var btnclasses = cn(createBtnClasses({
    size: size
  }), selectBtnTypeClass(), styles.IconButton, _defineProperty({}, styles.danger, danger));
  return /*#__PURE__*/React.createElement("button", _extends({}, btnProps, {
    className: btnclasses,
    ref: ref
  }), /*#__PURE__*/React.createElement(SvgNormalizer, {
    size: size
  }, icon));
});
IconButton.propTypes = {
  /**
   * Button uses the provided icon.
   * Accepts an inline SVG or SVG wrapped React component
   */
  icon: PropTypes.element.isRequired,

  /**
   * Applies primary brand color
   */
  primary: PropTypes.bool,

  /**
   * Renders as danger button
   */
  danger: PropTypes.bool,

  /**
   * Renders a nude icon button
   */
  nude: PropTypes.bool,

  /**
   * size of the IconButton
   */
  size: PropTypes.oneOf(["s", "m", "l"])
};
IconButton.defaultProps = {
  size: "m",
  nude: false
};

export default Button;
export { IconButton };
